import React, { useState } from 'react';
import SensorsPage from './SensorsPage';
import UsersPage from './UsersPage';
import MissionsPage from './MissionsPage';
import MissionView from './MissionView'; // import your MissionView component
import {
  UsersIcon,
  PowerIcon,
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
  SignalIcon,
  StarIcon
} from '@heroicons/react/24/solid';
import { TbTargetArrow } from "react-icons/tb";
import './theme.css';

function Dashboard({ token, role, logout }) {
  const [currentView, setCurrentView] = useState('missions');
  const [expanded, setExpanded] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState(null);

  const navItems = [
    {
      id: 'missions',
      label: 'Mission Management',
      icon: TbTargetArrow,
    },
    {
      id: 'sensors',
      label: 'Sensor Management',
      icon: SignalIcon,
    },
    ...(role === 'admin'
      ? [{ id: 'users', label: 'User Management', icon: UsersIcon }]
      : []),
  ];

  return (
    <div className="h-screen flex font-[var(--font-ui)] bg-[var(--ink-950)] text-[var(--text-primary)]">
      {/* Sidebar */}
      <aside
        className={`relative flex flex-col bg-[var(--surface-900)] border-r border-[var(--surface-800)] p-3 min-h-full transition-all duration-300`}
      >
        {/* Expand / Collapse Toggle + Wordmark */}
        <div className="mb-4 flex items-center gap-3">
          <button
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            className="p-2 rounded hover:bg-[var(--surface-800)] transition-colors flex items-center px-3 py-2"
          >
            {expanded ? (
              <ChevronDoubleLeftIcon className="w-5 h-5 text-[var(--text-muted)]" />
            ) : (
              <ChevronDoubleRightIcon className="w-5 h-5 text-[var(--text-muted)]" />
            )}
          </button>
          {expanded && (
            <div className="flex items-center gap-2 text-[var(--text-primary)]">
              <span className="text-lg font-bold">Signetry</span>
            </div>
          )}
        </div>

        <div className="border-t border-[var(--surface-800)] mb-4"></div>

        {/* Navigation */}
        <nav className="flex flex-col gap-2">
          {navItems.map(({ id, label, icon: Icon }) => (
            <div key={id} className="relative group">
              <button
                onClick={() => {
                  setCurrentView(id);
                  setSelectedMissionId(null); // reset selected mission when changing nav
                }}
                aria-label={label}
                className={`flex items-center h-10 w-full px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentView === id
                    ? 'bg-[var(--signal-cyan)] text-[var(--ink-950)] shadow-md'
                    : 'text-[var(--text-muted)] hover:bg-[var(--surface-800)] hover:text-[var(--text-primary)]'
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {expanded && <span className="ml-3 whitespace-nowrap">{label}</span>}
              </button>

              {!expanded && (
                <div role="tooltip" className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-[var(--surface-800)] text-[var(--text-primary)] rounded shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  {label}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div className="mt-auto pt-6 border-t border-[var(--surface-800)]">
          <div className="relative group">
            <button
              onClick={logout}
              aria-label="Logout"
              className="flex items-center justify-center h-10 w-full px-3 py-2 rounded-md font-medium text-[var(--status-error)] hover:text-[var(--text-primary)] border border-[var(--status-error)] hover:border-[var(--text-primary)] transition-all duration-200"
            >
              <PowerIcon className="w-5 h-5 shrink-0" />
              {expanded && <span className="ml-3 whitespace-nowrap">Logout</span>}
            </button>

            {!expanded && (
              <div role="tooltip" className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-[var(--surface-800)] text-[var(--text-primary)] rounded shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                Logout
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto bg-[var(--ink-950)] flex flex-col">
        {currentView === 'missions' && role === 'admin' && (
          <MissionsPage
            token={token}
            onViewMission={(id) => {
              setSelectedMissionId(id);
              setCurrentView('mission-view');
            }}
          />
        )}
        {currentView === 'sensors' && <SensorsPage token={token} role={role} />}
        {currentView === 'users' && role === 'admin' && <UsersPage token={token} />}
        {currentView === 'mission-view' && selectedMissionId && (
          <MissionView token={token} missionId={selectedMissionId} onBack={() => setCurrentView('missions')} />
        )}
      </main>
    </div>
  );
}

export default Dashboard;
